import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BiPurchaseTag, BiReceipt } from "react-icons/bi";
import { MdOutlineDiscount } from "react-icons/md";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import DashboardTiles from "../Dashboard/DashboardTiles";
import ReportsChart from "./ReportsChart";
import ReportsDateFilter from "./ReportsDateFilter";
import { computeDateRange } from "./reportFilters";

export default function PurchaseReportTab() {
  const today = useMemo(() => new Date(), []);
  const [rangeMode, setRangeMode] = useState("month");
  const [singleDate, setSingleDate] = useState(today);
  const [dateRange, setDateRange] = useState([
    new Date(today.getFullYear(), today.getMonth(), 1),
    today,
  ]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const { startDate, endDate } = useMemo(
    () => computeDateRange(rangeMode, today, singleDate, dateRange),
    [rangeMode, singleDate, dateRange, today]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchReport = async () => {
      setLoading(true);
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        const response = await backendVerifiedUser.get("/reports/purchases", { params });
        if (!cancelled) setReport(response.data);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReport();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const summary = report?.summary || {};

  const kpiData = [
    { amount: summary.totalPurchaseOrders || 0, title: "Total Purchase Orders", icon: <BiReceipt /> },
    { amount: summary.totalPurchaseAmount || 0, title: "Total Purchase Amount", icon: <BiPurchaseTag />, prefix: "RS" },
    { amount: summary.totalDiscount || 0, title: "Total Discount", icon: <MdOutlineDiscount />, prefix: "RS" },
    { amount: summary.totalAmount || 0, title: "Net Amount", icon: <BiPurchaseTag />, prefix: "RS" },
  ];

  const handleExport = () => {
    if (!report) return;

    const summaryRows = [
      ["Metric", "Value"],
      ["Date Range", `${startDate || "-"} to ${endDate || "-"}`],
      ["Total Purchase Orders", summary.totalPurchaseOrders || 0],
      ["Total Purchase Amount", summary.totalPurchaseAmount || 0],
      ["Total Discount", summary.totalDiscount || 0],
      ["Net Amount", summary.totalAmount || 0],
    ];

    const vendorRows = [
      ["Vendor", "Orders", "Total Amount"],
      ...(report.byVendor || []).map((v) => [v.vendor_name, v.orderCount, v.totalAmount]),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(vendorRows), "By Vendor");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `purchase-report-${startDate || "all"}-to-${endDate || "all"}.xlsx`);
  };

  return (
    <div>
      <div className="flex items-center justify-end mb-3">
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          className="p-primary-btn"
          onClick={handleExport}
          disabled={!report}
        />
      </div>

      <ReportsDateFilter
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        singleDate={singleDate}
        setSingleDate={setSingleDate}
        dateRange={dateRange}
        setDateRange={setDateRange}
        loading={loading}
      />

      {report && (
        <>
          <div className="mb-3 font-semibold text-black/50 text-lg ml-1">Summary</div>
          <DashboardTiles data={kpiData} />

          <div className="mt-8">
            <div className="my-3 font-semibold text-black/50 text-xl ml-1">Purchase Trend</div>
            <div className="card shadow-md p-4">
              {report.chartData && report.chartData.length > 0 ? (
                <ReportsChart data={report.chartData.map((c) => ({ label: c.label, orders: c.orders, sales: c.amount }))} />
              ) : (
                <div className="text-gray-400 text-sm py-8 text-center">No data for the selected filters.</div>
              )}
            </div>
          </div>

          <div className="mt-8">
            <div className="my-3 font-semibold text-black/50 text-xl ml-1">Purchases by Vendor</div>
            <div className="card shadow-md">
              <DataTable value={report.byVendor || []} stripedRows size="small" emptyMessage="No purchases for the selected filters.">
                <Column field="vendor_name" header="Vendor" />
                <Column field="orderCount" header="Orders" />
                <Column
                  field="totalAmount"
                  header="Total Amount"
                  body={(row) => `RS ${Number(row.totalAmount || 0).toLocaleString("en-IN")}`}
                />
              </DataTable>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
